import * as React from 'react'

import {classNames, createOrCallElement} from '@base/react-utils'

import {DOMEventsProps} from '@base-uikit/types'

import {grid, getWidthClassName, getHeightClassName} from './grid'
import type {SizeProps, AlignType, JustifyType, GutterType} from './types'

export type ContainerProps = {
  component?: string | Function
  className?: string
  style?: React.CSSProperties
  shrinkable?: boolean
  growable?: boolean
  children?: React.ReactNode
} & DOMEventsProps

export type ColumnProps = SizeProps & {
  alignSelf?: AlignType
  component?: string | Function
  className?: string
  style?: React.CSSProperties
  children?: React.ReactNode
} & DOMEventsProps

export type RowProps = ColumnProps & {
  // vertical alignment
  align?: AlignType
  // row vertical alignment
  rowAlign?: AlignType
  // horizontal justification
  justify?: JustifyType
  gutter?: GutterType
  rowGutter?: GutterType
  // layout columns in reverse order
  reverse?: boolean
  wrapperClassName?: string
}

export type OffsetProps = ColumnProps & {
  children?: React.ReactNode
}

export const Container = (props: ContainerProps) => {
  const {
    component = 'div',
    className,
    shrinkable,
    growable,
    ...otherProps
  } = props
  return createOrCallElement(component, {
    className: classNames(
      grid.container,
      shrinkable && grid.shrinkableContainer,
      growable && grid.growableContainer,
      className
    ),
    ...otherProps,
  })
}

export function Column(props: ColumnProps) {
  const {
    component = 'div',
    width,
    height,
    alignSelf,
    className,
    ...otherProps
  } = props

  let columnClass = className || ''
  columnClass += ' ' + getWidthClassName(width)
  columnClass += ' ' + getHeightClassName(height)
  if (alignSelf) {
    columnClass += ' ' + (grid[`alignSelf_${alignSelf}`] || '')
  }
  return createOrCallElement(component, {
    ...otherProps,
    className: columnClass,
  })
}

function renderRow(rowClassName, props) {
  const {
    children,
    className,
    wrapperClassName,
    align,
    rowAlign,
    justify,
    reverse,
    gutter,
    rowGutter,
    ...otherProps
  } = props

  let finalClassName = rowClassName
  if (align) {
    finalClassName += ' ' + (grid[`align_${align}`] || '')
  }
  if (rowAlign) {
    finalClassName += ' ' + (grid[`rowAlign_${rowAlign}`] || '')
  }
  if (justify) {
    finalClassName += ' ' + (grid[`justify_${justify}`] || '')
  }
  if (gutter) {
    finalClassName += ' ' + (grid[`gutter_${gutter}`] || '')
  }
  if (rowGutter) {
    finalClassName += ' ' + (grid[`rowGutter_${rowGutter}`] || '')
  }
  if (reverse) {
    finalClassName += ' ' + grid.reverse
  }
  if (wrapperClassName) {
    finalClassName += ' ' + wrapperClassName
  }
  // Row is rendered as a column, because they share alignment properties
  return Column({
    width: '12',
    ...otherProps,
    className: `${grid.rowContainer} ${className || ''}`,
    children: <div className={finalClassName}>{children}</div>,
  })
}

export function Row(props: RowProps) {
  return renderRow(grid.row, props)
}

export function Rows(props: RowProps) {
  return renderRow(grid.rows, props)
}

export function Offset(props: OffsetProps) {
  return Column({width: 'fill', ...props})
}
